import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function ScentPresetModal({ preset, notes, onClose, onSave }) {
  const isEdit = !!preset;
  const [form, setForm] = useState({
    name: preset?.name || '',
    vibe: preset?.vibe || 'Fresh / Clean',
    description: preset?.description || '',
    main_note: preset?.main_note || '',
    supporting_notes: preset?.supporting_notes?.join(', ') || '',
    icon: preset?.icon || '',
    sort_order: preset?.sort_order ?? 0,
    is_active: preset?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: parseInt(form.sort_order) || 0,
        supporting_notes: form.supporting_notes.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await axios.put(`${API}/admin/scent-presets/${preset.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Preset updated');
      } else {
        await axios.post(`${API}/admin/scent-presets`, payload, { headers: getAuthHeaders() });
        toast.success('Preset created');
      }
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="scent-preset-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isEdit ? 'Edit Preset' : 'Add Preset'}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <input type="text" placeholder="Preset Name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="preset-name-input" />
          <textarea placeholder="Description (Taglish ok)" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full bg-transparent border border-white/20 text-white py-2 px-3 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30 resize-none h-16"
            data-testid="preset-description-input" />
          <select value={form.vibe} onChange={e => setForm({ ...form, vibe: e.target.value })}
            className="w-full bg-[#141414] border border-white/20 text-white text-sm px-3 py-2" data-testid="preset-vibe-select">
            <option value="Fresh / Clean">Fresh / Clean</option>
            <option value="Sweet / Gourmand">Sweet / Gourmand</option>
            <option value="Woody / Masculine">Woody / Masculine</option>
            <option value="Floral / Feminine">Floral / Feminine</option>
          </select>
          <div>
            <label className="text-white/40 text-xs block mb-1">Main Note (ID)</label>
            <select value={form.main_note} onChange={e => setForm({ ...form, main_note: e.target.value })}
              className="w-full bg-[#141414] border border-white/20 text-white text-sm px-3 py-2" data-testid="preset-main-note-select">
              <option value="">Select main note</option>
              {notes.map(n => <option key={n.id} value={n.id}>{n.name} ({n.category})</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs block mb-1">Supporting Notes (comma-separated IDs)</label>
            <input type="text" value={form.supporting_notes}
              onChange={e => setForm({ ...form, supporting_notes: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-xs focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
              placeholder="e.g. lavender, musk, cedar"
              data-testid="preset-supporting-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Icon (briefcase/heart/crown/flower)" value={form.icon}
              onChange={e => setForm({ ...form, icon: e.target.value })}
              className="bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
              data-testid="preset-icon-input" />
            <input type="number" placeholder="Sort Order" value={form.sort_order}
              onChange={e => setForm({ ...form, sort_order: e.target.value })}
              className="bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              data-testid="preset-order-input" />
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-white/60 text-sm">Active</span>
            <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="preset-active-toggle" />
          </div>
        </div>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleSave} disabled={saving}
            className="w-full bg-[#D4AF37] text-black py-2.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            data-testid="save-preset-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Preset' : 'Create Preset'}
          </button>
        </div>
      </div>
    </div>
  );
}
